/* eslint-disable @typescript-eslint/no-explicit-any */
import { api_service } from '../api/api-service';
import { api_urls } from '../api/api-urls';

export interface ContractApiResponse {
    Id: string;
    UserId?: string;
    AccountType?: string;
    AspireRecordId: string;
    AspireId: string;
    AssetDesc?: string;
    IsPrimary: boolean;
    AspireOId: string;
    EntityOId?: string;
    AspireEntityOId?: string;
    PrimaryCustomerName?: string;
    Status: number;
    StatusDescr: string;
    ProgramId: number;
    State: string;
    City: string;
    Country: string;
    PostalCode: string;
    PrincipleAmount: string;
    OutstandingBalance: string;
    Yield: string;
    Term: number;
    EMIAmount: string;
    IsLoan: boolean;
    AutoPay: boolean;
    Frequency: string;
    BookingDate: string;
    MaturityDate: string;
    NumberOfPayments: number;
    RemainingPayments?: number;
    PaymentsReceived: number;
    LastPaymentDate: string | null;
    LastPaymentAmount: string | null;
    PastDue: string;
    NextPaymentDate: string | null;
    NextPaymentAmount: string | null;
    CurrentPaymentDate: string | null;
    YearToDateIntrest: string;
    YearToDatePrincipal: string | null;
    PrevYearIntrest: string;
    PrevYearPrincipal: string;
    TotalIntrestPaid: string;
    TotalPrincipalPaid: string;
    LastRetrievalTime?: string;
    CreatedTime: string;
    UpdatedTime: string;
    AutopayInProgress: boolean;
    CurrentDue: string;
    RemainingPrincipal?: string;
    AutoPayResumeDate?: string | null;
    Address1?: string;
    AddressLine1?: string;
    dbContractId?: string;
}

export interface ApiResponse {
    statusCode: number;
    data: ContractApiResponse[];
    message?: string;
}

/**
 * Normalizes a list-shaped API response: unwraps axios' `response.data`, and tolerates the
 * backend returning either `{ statusCode, data: [...] }` or a bare array. Shared by the
 * contract list and amortization schedule endpoints, which both follow this same shape.
 */
const normalizeListApiResponse = <T>(response: any): { statusCode: number; data: T[] } => {
    const responseData = response?.data || response;

    if (responseData.statusCode && Array.isArray(responseData.data)) {
        return responseData;
    } else if (Array.isArray(responseData)) {
        return { statusCode: 200, data: responseData };
    }
    return { statusCode: 200, data: [] };
};

/**
 * Get all contracts (both active and closed)
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with contracts response
 */
export const getAllContracts = async (showLoader: boolean = true): Promise<ApiResponse> => {
    try {
        const response: any = await api_service.post({
            endPoint: api_urls.GetContractActiveList,
            showLoader,
        });
        return normalizeListApiResponse<ContractApiResponse>(response);
    } catch (error: any) {
        console.error('Error fetching contracts:', error);
        throw error;
    }
};

export interface SingleContractResponse {
    statusCode: number;
    data: ContractApiResponse;
    message?: string;
}

/**
 * Get contract by ID
 * @param id - Contract ID (AspireId or database Id)
 * @param showLoader - Whether to show loading indicator (default: true)
 * @returns Promise with single contract response
 */
export const getContractById = async (
    id: string | number,
    showLoader: boolean = true,
): Promise<SingleContractResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: `${api_urls.GetContractById}/${id}`,
            showLoader,
        });
        // Handle axios response structure
        const responseData = response?.data || response;

        // If responseData has statusCode and data, return it
        if (responseData.statusCode && responseData.data) {
            return responseData;
        } else if (responseData.Id || responseData.AspireId) {
            // If it's a single contract object, wrap it
            return { statusCode: 200, data: responseData };
        }
        throw new Error('Invalid contract response');
    } catch (error: any) {
        console.error('Error fetching contract by ID:', error);
        throw error;
    }
};

export interface AmortizationScheduleItem {
    PaymentNumber?: number;
    PaymentDate?: string;
    // PaymentAmount?: number | string;
    PrincipleAmount?: number | string;
    InterestAmount?: number | string;
    Balance?: number | string;
    Amount?: number;
    EventType?: string;
    OutstandingBalance?: any;
}

export interface AmortizationScheduleResponse {
    statusCode: number;
    data: AmortizationScheduleItem[];
    totalCount?: number;
    message?: string;
}

/**
 * Get amortization schedule for a contract
 * @param contractId - Contract ID
 * @param showLoader - Whether to show loading indicator (default: true)
 * @param page - Page number (default: 1)
 * @param limit - Number of items per page (default: 10)
 * @returns Promise with amortization schedule response
 */
export const getContractAmortizationSchedule = async (
    contractId: string | number,
    showLoader: boolean = true,
    page: number = 1,
    limit: number = 10,
): Promise<AmortizationScheduleResponse> => {
    try {
        const response: any = await api_service.get({
            endPoint: `${api_urls.GetContractAmortization}/${contractId}`,
            showLoader,
            payLoad: {
                page,
                limit,
            },
        });
        return normalizeListApiResponse<AmortizationScheduleItem>(response);
    } catch (error: any) {
        console.error('Error fetching amortization schedule:', error);
        throw error;
    }
};
